import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GraduationCap, Play, Send } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { getApiError } from '../services/api'
import { useAuthStore } from '../stores/authStore'

const initialForm = {
  url: '',
  titulo: '',
  mensagem: '',
  resumo: '',
  exercicios: '',
  instituicao_id: '',
  turma_id: ''
}

function RecomendacaoProfessor() {
  const queryClient = useQueryClient()
  const accessToken = useAuthStore(state => state.accessToken)
  const [form, setForm] = useState(initialForm)

  const { data: destinos = [], isLoading: loadingDestinos } = useQuery({
    queryKey: ['recomendacoes', 'destinos'],
    queryFn: async () => (await api.get('/recomendacoes/destinos')).data.destinos,
    enabled: Boolean(accessToken)
  })

  const { data: minhasRecomendacoes = [] } = useQuery({
    queryKey: ['recomendacoes', 'minhas'],
    queryFn: async () => (await api.get('/recomendacoes/minhas')).data.recomendacoes,
    enabled: Boolean(accessToken && destinos.length)
  })

  useEffect(() => {
    if (!destinos.length || form.instituicao_id) return
    const primeiro = destinos[0]
    setForm(current => ({
      ...current,
      instituicao_id: primeiro.instituicao.id,
      turma_id: primeiro.papel === 'professor' && primeiro.turmas.length === 1 ? primeiro.turmas[0].id : ''
    }))
  }, [destinos, form.instituicao_id])

  const recomendar = useMutation({
    mutationFn: async () => {
      const exercicios = form.exercicios
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
        .map(enunciado => ({ enunciado, resposta: '', explicacao: '' }))

      return api.post('/recomendacoes', { ...form, exercicios })
    },
    onSuccess: () => {
      toast.success('Link recomendado aos alunos da turma!')
      setForm(current => ({ ...current, url: '', titulo: '', mensagem: '', resumo: '', exercicios: '' }))
      queryClient.invalidateQueries({ queryKey: ['recomendacoes'] })
    },
    onError: error => toast.error(getApiError(error, 'Não foi possível recomendar a aula.'))
  })

  if (loadingDestinos) {
    return <p className="card max-w-3xl text-sm text-gray-500">Carregando suas turmas...</p>
  }

  if (!destinos.length) {
    return (
      <section className="card max-w-3xl border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
        <div className="flex items-start gap-3">
          <GraduationCap size={18} className="mt-0.5 shrink-0 text-amber-600" />
          <div>
            <h2 className="text-sm font-medium text-amber-900 dark:text-amber-100">Vínculo com uma turma necessário</h2>
            <p className="mt-1 text-xs leading-5 text-amber-700 dark:text-amber-300">Para recomendar links, aceite o convite de uma instituição e peça ao gestor para incluir você em uma turma. Assim, somente os alunos vinculados receberão a indicação.</p>
          </div>
        </div>
      </section>
    )
  }

  const destinoSelecionado = destinos.find(item => item.instituicao.id === form.instituicao_id)
  const professorSemTurma = destinoSelecionado?.papel === 'professor' && destinoSelecionado.turmas.length === 0

  return (
    <div className="space-y-6">
      <section className="card max-w-3xl">
        <div className="mb-4 flex items-center gap-2">
          <GraduationCap size={18} />
          <div>
            <h2 className="text-sm font-medium">Nova recomendação</h2>
            <p className="text-xs text-gray-500">Cole um link do YouTube e escolha a turma que deve recebê-lo.</p>
          </div>
        </div>

        <form className="space-y-3" onSubmit={event => { event.preventDefault(); recomendar.mutate() }}>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1">
              <span className="text-xs font-medium text-gray-500">Instituição</span>
              <select
                className="input-field"
                value={form.instituicao_id}
                onChange={event => {
                  const destino = destinos.find(item => item.instituicao.id === event.target.value)
                  setForm(current => ({
                    ...current,
                    instituicao_id: event.target.value,
                    turma_id: destino?.papel === 'professor' && destino.turmas.length === 1 ? destino.turmas[0].id : ''
                  }))
                }}
                required
              >
                {destinos.map(item => <option key={item.instituicao.id} value={item.instituicao.id}>{item.instituicao.nome}</option>)}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium text-gray-500">Alunos que receberão</span>
              <select
                className="input-field"
                value={form.turma_id}
                onChange={event => setForm(current => ({ ...current, turma_id: event.target.value }))}
                required={destinoSelecionado?.papel === 'professor'}
              >
                <option value="">{destinoSelecionado?.papel === 'gestor' ? 'Todos da instituição' : 'Escolha uma turma'}</option>
                {(destinoSelecionado?.turmas || []).map(turma => <option key={turma.id} value={turma.id}>{turma.nome} · {turma.ano_letivo}</option>)}
              </select>
            </label>
          </div>

          {professorSemTurma && (
            <p className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">Peça ao gestor para incluir você em uma turma antes de enviar recomendações.</p>
          )}

          <label className="block space-y-1">
            <span className="text-xs font-medium text-gray-500">Link da aula</span>
            <input className="input-field" type="url" placeholder="https://www.youtube.com/watch?v=..." value={form.url} onChange={event => setForm(current => ({ ...current, url: event.target.value }))} required />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-gray-500">Título</span>
            <input className="input-field" placeholder="Ex.: Introdução às equações do 2º grau" value={form.titulo} onChange={event => setForm(current => ({ ...current, titulo: event.target.value }))} required maxLength={300} />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-gray-500">Por que assistir? <span className="font-normal text-gray-400">(opcional)</span></span>
            <textarea className="input-field min-h-20" placeholder="Explique aos alunos como este conteúdo ajudará no aprendizado." value={form.mensagem} onChange={event => setForm(current => ({ ...current, mensagem: event.target.value }))} maxLength={2000} />
          </label>
          <details className="rounded-xl border border-gray-100 p-3 dark:border-zinc-800">
            <summary className="cursor-pointer text-xs font-medium text-gray-600 dark:text-zinc-300">Adicionar resumo ou exercícios</summary>
            <div className="mt-3 space-y-3">
              <textarea className="input-field min-h-24" placeholder="Resumo da aula (opcional)" value={form.resumo} onChange={event => setForm(current => ({ ...current, resumo: event.target.value }))} />
              <textarea className="input-field min-h-24" placeholder="Exercícios, um por linha (opcional)" value={form.exercicios} onChange={event => setForm(current => ({ ...current, exercicios: event.target.value }))} />
            </div>
          </details>
          <button className="btn-primary flex w-full items-center justify-center gap-2" disabled={recomendar.isLoading || professorSemTurma || (destinoSelecionado?.papel === 'professor' && !form.turma_id)}>
            <Send size={15} /> {recomendar.isLoading ? 'Enviando...' : 'Recomendar aos alunos'}
          </button>
        </form>
      </section>

      {minhasRecomendacoes.length > 0 && (
        <section className="max-w-5xl">
          <h2 className="mb-3 text-sm font-medium">Recomendações enviadas</h2>
          <div className="grid gap-2 md:grid-cols-2">
            {minhasRecomendacoes.map(item => (
              <Link key={item.id} to={`/recomendacao/${item.id}`} className="card flex items-center gap-3">
                <Play size={17} className="shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{item.titulo}</p>
                  <p className="mt-1 truncate text-xs text-gray-500">{item.instituicao?.nome || 'Recomendação geral'}{item.turma ? ` · ${item.turma.nome}` : ''}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

export default RecomendacaoProfessor
